import React, { useContext, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Box, Text, TextButton } from '@wix/design-system';
import { AppContextProvider } from '../AppLoader';
import { copyPageLink } from '../../utils/inAppBrowser';
import { dataHooks } from '../../dataHooks';

// Wix dark navy — the card background in the design.
const DARK_NAVY = '#000624';

/**
 * Inline dark notice shown directly below the Google button when the page runs
 * inside an in-app browser Google refuses to authenticate (e.g. LinkedIn's
 * webview). The system-browser hand-off already fired on the Google click
 * (see GoogleLoginStore.onClickCustomLoginButton), so the notice's action is a
 * copy button: it copies the page link (with all its params) for the user to
 * paste in a real browser, flipping to a "Copied!" confirmation. The copy
 * strings reuse the already-translated `unsupported.browser.for.social.*` keys.
 *
 * Typography and layout come from the design-system Box/Text/TextButton
 * components (with the `light` skin for the dark card) so it matches the rest
 * of the login UI. Renders nothing until the Google button is clicked in such a
 * browser.
 */
export const GoogleInAppBrowserNotice: React.FC = observer(() => {
  const {
    rootStore: { socialAuthStore, displayStore },
  } = useContext(AppContextProvider);
  const { t } = useTranslation();
  const [linkCopied, setLinkCopied] = useState(false);

  if (!socialAuthStore.showGoogleInAppBrowserNotice) {
    return null;
  }

  const onCopyLink = () => {
    void copyPageLink();
    setLinkCopied(true);
  };

  // Match the Google button's width in the vertical button stack; span the
  // full social area in the horizontal icon layout.
  const width = displayStore.isVerticalLayout
    ? displayStore.socialButtonElementWidth
    : '100%';

  return (
    <Box
      direction="vertical"
      width={width}
      boxSizing="border-box"
      backgroundColor={DARK_NAVY}
      borderRadius={8}
      padding="20px"
      marginBottom="12px"
      textAlign="start"
      dataHook={dataHooks.socials.googleInAppBrowserNotice}
    >
      <Text size="medium" weight="bold" light>
        {t(
          'social.login.in_app_browser.google_notice.title',
          'To continue with Google, open this page in your browser',
        )}
      </Text>
      <Box marginTop="8px">
        <Text size="small" light>
          {t(
            'social.login.in_app_browser.google_notice.body',
            "Google doesn't allow this from inside another app. You can also continue with email, Facebook or Apple here.",
          )}
        </Text>
      </Box>
      <Box marginTop="16px">
        <TextButton
          skin="light"
          underline="always"
          weight="bold"
          size="small"
          onClick={onCopyLink}
          dataHook={dataHooks.socials.googleInAppBrowserNoticeCta}
        >
          {linkCopied
            ? t('unsupported.browser.for.social.copy.link.success', 'Copied!')
            : t('unsupported.browser.for.social.copy.link', 'Copy Link')}
        </TextButton>
      </Box>
    </Box>
  );
});
